import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, CheckCircle, ArrowRight, TrendingUp, Shield, Coins, BarChart3 } from 'lucide-react';
import Navigation from '../../components/Navigation';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const ZlatnaKlauzula = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const goldColor = '#CFB53B';
  const goldDark = '#A8922A';
  const goldLight = '#E8D56A';

  const features = [
    t('zlatnaKlauzula.feature1'),
    t('zlatnaKlauzula.feature2'),
    t('zlatnaKlauzula.feature3'),
    t('zlatnaKlauzula.feature4'),
    t('zlatnaKlauzula.feature5'),
    t('zlatnaKlauzula.feature6'),
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Hero Section - golden gradient */}
      <section
        className="relative text-white py-12 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${goldDark} 0%, ${goldColor} 50%, ${goldLight} 100%)` }}
      >
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0 animate-pulse"
            style={{ background: `linear-gradient(135deg, #8B6914, ${goldColor}, #F5E27A)` }}
          ></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center border-2 border-white/40">
                <Coins size={32} className="text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white drop-shadow-md">
              {t('zlatnaKlauzula.heroTitle')}
            </h1>
            <p className="text-xl md:text-2xl mb-8" style={{ color: 'rgba(255,255,255,0.9)' }}>
              {t('zlatnaKlauzula.heroSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/berza"
                className="bg-white px-8 py-4 rounded-lg font-semibold hover:bg-yellow-50 transition-colors inline-flex items-center justify-center gap-2"
                style={{ color: goldDark }}
              >
                {t('zlatnaKlauzula.viewMarketplace')}
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/zapocni-trgovinu"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-colors inline-flex items-center justify-center"
              >
                {t('zlatnaKlauzula.goToTrading')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="pt-10 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* Left: Description */}
            <div>
              <p className="text-lg text-gray-600 mb-6">{t('zlatnaKlauzula.desc1')}</p>
              <p className="text-lg text-gray-600 mb-6">{t('zlatnaKlauzula.desc2')}</p>
              <p className="text-lg text-gray-600 mb-8">{t('zlatnaKlauzula.desc3')}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {features?.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="flex-shrink-0" size={24} style={{ color: goldColor }} />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Info card */}
            <div
              className="rounded-2xl p-8 text-white"
              style={{ background: `linear-gradient(135deg, ${goldDark} 0%, ${goldColor} 100%)` }}
            >
              <div className="flex items-center gap-3 mb-4">
                <Scale size={32} className="text-white" />
                <h3 className="text-3xl font-bold">{t('zlatnaKlauzula.howItWorks')}</h3>
              </div>
              <p className="mb-6" style={{ color: 'rgba(255,255,255,0.9)' }}>
                {t('zlatnaKlauzula.howItWorksDesc')}
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{t('zlatnaKlauzula.benefit1Title')}</div>
                    <div className="text-xs" style={{ color: 'rgba(255,255,255,0.8)' }}>{t('zlatnaKlauzula.benefit1Desc')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{t('zlatnaKlauzula.benefit2Title')}</div>
                    <div className="text-xs" style={{ color: 'rgba(255,255,255,0.8)' }}>{t('zlatnaKlauzula.benefit2Desc')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BarChart3 size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{t('zlatnaKlauzula.benefit3Title')}</div>
                    <div className="text-xs" style={{ color: 'rgba(255,255,255,0.8)' }}>{t('zlatnaKlauzula.benefit3Desc')}</div>
                  </div>
                </div>
              </div>

              <Link
                to="/berza"
                className="block w-full bg-white px-6 py-4 rounded-lg font-semibold hover:bg-yellow-50 transition-colors text-center"
                style={{ color: goldDark }}
              >
                {t('zlatnaKlauzula.exploreVouchers')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mechanism Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('zlatnaKlauzula.mechanismTitle')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{t('zlatnaKlauzula.mechanismSubtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative bg-gray-50 rounded-2xl p-6 border-t-4" style={{ borderColor: goldColor }}>
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg mb-4"
                style={{ background: goldColor }}
              >
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('zlatnaKlauzula.step1Title')}</h3>
              <p className="text-gray-600">{t('zlatnaKlauzula.step1Desc')}</p>
            </div>

            {/* Step 2 */}
            <div className="relative bg-gray-50 rounded-2xl p-6 border-t-4" style={{ borderColor: goldColor }}>
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg mb-4"
                style={{ background: goldColor }}
              >
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('zlatnaKlauzula.step2Title')}</h3>
              <p className="text-gray-600">{t('zlatnaKlauzula.step2Desc')}</p>
            </div>

            {/* Step 3 */}
            <div className="relative bg-gray-50 rounded-2xl p-6 border-t-4" style={{ borderColor: goldColor }}>
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg mb-4"
                style={{ background: goldColor }}
              >
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('zlatnaKlauzula.step3Title')}</h3>
              <p className="text-gray-600">{t('zlatnaKlauzula.step3Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-20 text-white"
        style={{ background: `linear-gradient(135deg, ${goldDark} 0%, ${goldColor} 100%)` }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">{t('zlatnaKlauzula.ctaTitle')}</h2>
          <p className="text-xl mb-8" style={{ color: 'rgba(255,255,255,0.9)' }}>{t('zlatnaKlauzula.ctaSubtitle')}</p>
          <Link
            to={user ? '/berza' : '/register'}
            className="inline-flex items-center gap-2 bg-white px-8 py-4 rounded-lg font-semibold hover:bg-yellow-50 transition-colors"
            style={{ color: goldDark }}
          >
            {user ? t('zlatnaKlauzula.ctaBtn') : t('zlatnaKlauzula.ctaBtnGuest')}
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <footer className="text-white py-8" style={{ backgroundColor: '#23395B' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p style={{ color: '#94a3b8' }}>{t('startTrading.footerCopyright')}</p>
        </div>
      </footer>
    </div>
  );
};

export default ZlatnaKlauzula;
